import type { FieldErrors, FieldValues } from "react-hook-form";
import notification from "@app/services/notification";
import type { FlattenedFieldError, FormSchemaConfig } from "@src/components/form";
import { flattenFieldErrors } from "@src/components/form";

const LOG_PREFIX = "[BusinessForm/validation]";

function isDev(): boolean {
    return process.env.NODE_ENV !== "production";
}

function warnValidationDev(message: string, detail?: unknown): void {
    if (!isDev()) return;
    if (detail !== undefined) {
        console.warn(LOG_PREFIX, message, detail);
    } else {
        console.warn(LOG_PREFIX, message);
    }
}

/**
 * Безопасно читает `schema.fields` без «тихих» приведений типов.
 * При битой структуре в DEV — одно предупреждение с пояснением; в prod — `undefined`.
 */
function readSchemaFieldMap(
    schema: FormSchemaConfig<Record<string, unknown>> | undefined,
): Record<string, { label?: string }> | undefined {
    if (schema == null) {
        return undefined;
    }
    if (typeof schema !== "object" || Array.isArray(schema)) {
        warnValidationDev(
            "`schemaConfig` должен быть объектом `FormSchemaConfig`, а не массивом / примитивом — подписи полей в тосте недоступны.",
            schema,
        );
        return undefined;
    }
    const raw = schema.fields as unknown;
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
        warnValidationDev(
            "`schemaConfig.fields` должен быть плоским объектом `{ [fieldKey]: FieldSchema }`, как после `defineFormConfig`.",
            { fields: raw },
        );
        return undefined;
    }
    return raw as Record<string, { label?: string }>;
}

/**
 * Первый сегмент пути ошибки RHF, который можно считать именем поля формы.
 * Пропускает `root` и индексы массивов (`0`, `1`, …) — они не совпадают с ключами в `schema.fields`.
 */
export function pickMeaningfulFieldSegmentFromErrorPath(path: string): string | undefined {
    if (path === "" || path === "(form)") {
        return undefined;
    }
    for (const part of path.split(".")) {
        if (part === "" || part === "root") continue;
        if (/^\d+$/.test(part)) continue;
        return part;
    }
    return undefined;
}

/**
 * Заголовок поля для UI, если в схеме нет `label`: из `loginName` → «Login Name», из `full_name` → «Full Name».
 *
 * **Ограничение:** аббревиатуры и редкие стили имён могут выглядеть неидеально; тогда задайте явный `label` в `defineFormConfig`.
 */
export function humanizeFieldKeyForDisplay(key: string): string {
    const trimmed = key.trim();
    if (trimmed === "") {
        return key;
    }
    const withSpaces = trimmed.replace(/_/g, " ");
    const splitCamel = withSpaces.replace(/([a-z])([A-Z])/g, "$1 $2");
    const words = splitCamel.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
        return trimmed;
    }
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
}

type CaptionSource = "schema" | "resolved" | "humanized" | "none";

/** Опции подписи поля в стандартном тосте валидации {@link BusinessForm}. */
export type BusinessFormValidationCaptionOptions = {
    /**
     * Runtime-подпись из `formConfig.resolveFieldMeta` (reactive `label` в `defineFormConfig`).
     * @param segment первый значимый сегмент path ошибки RHF
     */
    resolveFieldLabel?: (segment: string) => string | undefined;
};

/**
 * Ищет подпись в схеме по пути ошибки: проходит сегменты по порядку, как ключи в `fields`
 * (первое совпадение с непустым `label`), а не только «верхний» ключ формы.
 */
function findSchemaLabelAlongPath(path: string, fieldMap: Record<string, { label?: string }>): string | undefined {
    for (const part of path.split(".")) {
        if (part === "" || part === "root") continue;
        if (/^\d+$/.test(part)) continue;
        const spec = fieldMap[part];
        if (spec?.label != null && String(spec.label).trim() !== "") {
            return String(spec.label).trim();
        }
    }
    return undefined;
}

function resolveCaptionForValidationLine(
    path: string,
    fieldMap: Record<string, { label?: string }> | undefined,
    captionOptions?: BusinessFormValidationCaptionOptions,
): { caption: string | undefined; source: CaptionSource } {
    const segment = pickMeaningfulFieldSegmentFromErrorPath(path);

    if (fieldMap != null) {
        const fromSchema = findSchemaLabelAlongPath(path, fieldMap);
        if (fromSchema != null) {
            return { caption: fromSchema, source: "schema" };
        }
        if (segment != null) {
            const fromResolved = captionOptions?.resolveFieldLabel?.(segment)?.trim();
            if (fromResolved != null && fromResolved !== "") {
                return { caption: fromResolved, source: "resolved" };
            }
        }
        if (segment != null) {
            const spec = fieldMap[segment];
            if (isDev() && spec != null) {
                warnValidationDev(
                    `По path есть ключ «${segment}» в schema, но ни у него, ни у сегментов path нет непустого label — в тосте подставлена авто-подпись; задайте label в defineFormConfig или resolveFieldMeta.`,
                    { path },
                );
            } else if (isDev() && spec == null) {
                warnValidationDev(
                    `Первый значимый сегмент «${segment}» path ошибки не найден в schema.fields — проверьте имена полей и schemaConfig.`,
                    { path, knownFieldKeys: Object.keys(fieldMap) },
                );
            }
        }
    } else if (isDev()) {
        warnValidationDev(
            "`schemaConfig` не передан — подпись в тосте из humanize ключа path; для точных названий используйте `formConfig` в BusinessForm.",
            { path },
        );
    }

    if (segment != null) {
        return { caption: humanizeFieldKeyForDisplay(segment), source: "humanized" };
    }

    if (isDev()) {
        warnValidationDev(
            "Не удалось выделить ключ поля из path (корневая ошибка или нестандартный путь) — в строке только сообщение валидатора.",
            { path },
        );
    }
    return { caption: undefined, source: "none" };
}

function normalizeValidationMessage(raw: unknown): string {
    if (raw == null) {
        return "";
    }
    if (typeof raw === "string") {
        return raw.trim();
    }
    if (typeof raw === "number" || typeof raw === "boolean") {
        return String(raw);
    }
    warnValidationDev(
        "Сообщение об ошибке поля имеет нестандартный тип — сериализовано для тоста; проверьте resolver (ожидается string).",
        raw,
    );
    if (typeof raw === "object") {
        try {
            return JSON.stringify(raw);
        } catch {
            return "";
        }
    }
    return "";
}

/**
 * Находит подпись поля в {@link FormSchemaConfig} по пути ошибки RHF (`loginName`, `groups.root`, …).
 * Обходит сегменты, пока не найдёт ключ из `fields` с непустым `label` (как и раньше, но без непроверенного cast всего `schema`).
 */
export function resolveFormSchemaLabelForErrorPath(
    path: string,
    schema: FormSchemaConfig<Record<string, unknown>> | undefined,
): string | undefined {
    const fieldMap = readSchemaFieldMap(schema);
    if (fieldMap == null) {
        return undefined;
    }
    return findSchemaLabelAlongPath(path, fieldMap);
}

/**
 * Контекст ошибки валидации на submit для колбэка `onError` у `BusinessForm`.
 * Расширяется при необходимости (лейблы, домен и т.д.).
 */
export type BusinessFormSubmitErrorContext<TFieldValues extends FieldValues = FieldValues> = {
    /** Дерево ошибок react-hook-form. */
    errors: FieldErrors<TFieldValues>;
    /** Результат {@link flattenFieldErrors} — плоские `path` + `message`. */
    flattenedErrors: FlattenedFieldError[];
    /** То же, что `flattenedErrors` (короткое имя в колбэках). */
    flattenErrors: FlattenedFieldError[];
};

/**
 * Обработчик ошибок валидации на уровне `BusinessForm` (не путать с «сырым» `onError` базового `Form` / react-hook-form).
 */
export type BusinessFormOnError<TFieldValues extends FieldValues = FieldValues> = (
    ctx: BusinessFormSubmitErrorContext<TFieldValues>,
) => void;

const EMPTY_BODY_FALLBACK = "Не удалось сформировать список ошибок. Проверьте валидацию формы или повторите попытку.";

/**
 * Собирает тело стандартного предупреждения: в списке нет сырых RHF path — подпись из `schema.label`, иначе {@link humanizeFieldKeyForDisplay}.
 *
 * **Когда в консоль (только DEV):** битый `schemaConfig`, несовпадение path и `fields`, нет `label`, странный `message`.
 */
export function formatBusinessFormValidationWarningBody<TFieldValues extends FieldValues>(
    ctx: BusinessFormSubmitErrorContext<TFieldValues>,
    schemaConfig?: FormSchemaConfig<Record<string, unknown>>,
    captionOptions?: BusinessFormValidationCaptionOptions,
): string {
    const fieldMap = readSchemaFieldMap(schemaConfig);

    if (ctx.flattenErrors.length === 0) {
        const errObj = ctx.errors as unknown;
        const keys =
            errObj != null && typeof errObj === "object" && !Array.isArray(errObj) ? Object.keys(errObj as object) : [];
        if (keys.length > 0) {
            warnValidationDev(
                "`flattenErrors` пуст при непустом дереве `errors` — см. также предупреждение `[Form/flattenFieldErrors]` (часто нет листьев с `message`).",
                { errorKeys: keys },
            );
        }
        return EMPTY_BODY_FALLBACK;
    }

    const lines: string[] = [];
    for (let i = 0; i < ctx.flattenErrors.length; i++) {
        const item = ctx.flattenErrors[i];
        const msg = normalizeValidationMessage(item.message);
        if (msg === "") {
            warnValidationDev("Пустое сообщение в плоской ошибке — строка пропущена.", { index: i, path: item.path });
            continue;
        }
        const { caption } = resolveCaptionForValidationLine(item.path, fieldMap, captionOptions);
        if (caption != null) {
            lines.push(`«${caption}» — ${msg}`);
        } else {
            lines.push(msg);
        }
    }

    if (lines.length === 0) {
        warnValidationDev("После фильтрации не осталось ни одной непустой строки сообщения.", ctx.flattenErrors);
        return EMPTY_BODY_FALLBACK;
    }

    return lines.map((line) => `- ${line}`).join("\n");
}

/**
 * Стандартное уведомление о провале валидации при submit (как в доменных модалках до выноса в `BusinessForm`).
 * Можно вызывать из своего {@link BusinessFormOnError}, если отключили автоматический тост, но хотите тот же текст.
 */
export function showDefaultBusinessFormValidationWarning<TFieldValues extends FieldValues>(
    ctx: BusinessFormSubmitErrorContext<TFieldValues>,
    heading = "Проверьте форму",
    schemaConfig?: FormSchemaConfig<Record<string, unknown>>,
    captionOptions?: BusinessFormValidationCaptionOptions,
): void {
    if (heading.trim() === "" && isDev()) {
        warnValidationDev("Передан пустой `heading` для уведомления — используется пустая строка как префикс.");
    }
    const body = formatBusinessFormValidationWarningBody(ctx, schemaConfig, captionOptions);
    notification.warning(`${heading}:\n${body}`);
}

/**
 * Собирает контекст для {@link BusinessFormOnError} из объекта ошибок RHF.
 */
export function toBusinessFormSubmitErrorContext<TFieldValues extends FieldValues>(
    errors: FieldErrors<TFieldValues>,
): BusinessFormSubmitErrorContext<TFieldValues> {
    if (errors == null) {
        warnValidationDev(
            "`toBusinessFormSubmitErrorContext` получил null/undefined вместо `FieldErrors` — контекст пустой.",
            errors,
        );
        return {
            errors: {} as FieldErrors<TFieldValues>,
            flattenedErrors: [],
            flattenErrors: [],
        };
    }
    if (typeof errors !== "object" || Array.isArray(errors)) {
        warnValidationDev(
            "`toBusinessFormSubmitErrorContext` ожидает объект ошибок RHF, получено не объект — контекст пустой.",
            errors,
        );
        return {
            errors: {} as FieldErrors<TFieldValues>,
            flattenedErrors: [],
            flattenErrors: [],
        };
    }

    const flattenedErrors = flattenFieldErrors(errors);
    return {
        errors,
        flattenedErrors,
        flattenErrors: flattenedErrors,
    };
}
